import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_CUSTOMERS,
  MN_JOBS,
  MN_RESOURCES,
  MODULE,
} from '../common';
import type { JobState, JobType, OrderState, Prisma } from '@prisma/client';
import {
  JobStateEnum,
  JobTypeEnum,
} from '@jobcost-monorepo/database/prisma/enums';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';

type Job = Prisma.$JobPayload['scalars'];

export type IJob = Job &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    customer: KeyValue;
  };

export interface IListJobFetchParams extends IListFetchParams {
  customerId?: number;
  resourceId?: number;
  jobType?: JobType;
  state?: JobState;
  orderState?: OrderState;
  startDate?: string;
  endDate?: string;
  isExpired?: boolean;
  isExpiring?: boolean;
  excludeRevenue?: boolean;
}

class JobRepository extends BaseRepository<IJob, IListJobFetchParams> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['code', 'description'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'code',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'description',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'customer.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'startDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'endDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 6,
        linkedField: 'jobType',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_JOBS,
        enumValues: JobTypeEnum,
      },
      {
        visible: true,
        position: 7,
        linkedField: 'state',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        enumValues: JobStateEnum,
      },
      {
        visible: true,
        position: 7,
        linkedField: 'excludeRevenue',
        type: '',
        align: 'center',
        width: 'w-full',
        sortable: false,
        formatType: 'boolean',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'jobType',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
        enumValues: JobTypeEnum,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'state',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
        enumValues: JobStateEnum,
      },
      {
        position: { x: 0, y: 2 },
        linkedField: 'customerId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_CUSTOMERS,
        translationModuleName: MN_CUSTOMERS,
      },
      {
        position: { x: 0, y: 3 },
        linkedField: 'resourceId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 4 },
        linkedField: 'startDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        position: { x: 1, y: 4 },
        linkedField: 'endDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        position: { x: 0, y: 5 },
        linkedField: 'excludeRevenue',
        width: 'w-full',
        formatType: 'tristate',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'code',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'description',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'customerId',
        linkedFieldForDefaultValue: 'customer.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_CUSTOMERS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'jobType',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: JobTypeEnum,
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'startDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 3 },
        linkedField: 'endDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'state',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: JobStateEnum,
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 5 },
        linkedField: 'estimatedCost',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 5 },
        linkedField: 'estimatedRevenue',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 6 },
        linkedField: 'excludeRevenue',
        type: 'string',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 7 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      code: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      description: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      customerId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      startDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      endDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      jobType: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      estimatedCost: z
        .union([
          z.number().nonnegative({ message: 'nonNegativeNumber' }),
          z.null(),
          z.undefined(),
        ])
        .optional(),
      estimatedRevenue: z
        .union([
          z.number().nonnegative({ message: 'nonNegativeNumber' }),
          z.null(),
          z.undefined(),
        ])
        .optional(),
      state: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      excludeRevenue: z.boolean({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default JobRepository;
